import { supabase } from "./supabase";

// --- Types ---

export interface UserGamification {
  user_id: string;
  current_streak: number;
  longest_streak: number;
  last_activity_date: string | null;
  xp_points: number;
  level: number;
  badges: string[];
  created_at: string;
  updated_at: string;
}

export interface XPEvent {
  id: string;
  user_id: string;
  action: string;
  xp_amount: number;
  created_at: string;
}

// --- XP Configuration ---

export const XP_REWARDS: Record<string, number> = {
  add_skill: 10,
  complete_mining_session: 25,
  swipe: 5,
  update_application_status: 10,
  link_story: 15,
  bonus_deep_mining: 15,
  bonus_new_territory: 20,
  bonus_story_rich: 10,
  daily_first_swipe: 10, // Extra XP: 15 total (5 base + 10 bonus)
  daily_first_mining_session: 25, // Extra XP: 50 total (25 base + 25 bonus)
  generate_resume: 20,
};

export const XP_ACTION_LABELS: Record<string, string> = {
  add_skill: "Added a skill",
  complete_mining_session: "Completed mining session",
  swipe: "Swiped on a job",
  update_application_status: "Updated application",
  link_story: "Linked a story",
  bonus_deep_mining: "Bonus: Complete Story",
  bonus_new_territory: "Bonus: New Territory",
  bonus_story_rich: "Bonus: Story Rich",
  daily_first_swipe: "Daily First Swipe Bonus",
  daily_first_mining_session: "Daily First Mining Bonus",
  generate_resume: "Generated a resume",
};

// --- Level Configuration ---

export const LEVEL_THRESHOLDS: Record<number, number> = {
  1: 0,
  2: 100,
  3: 300,
  4: 600,
  5: 1000,
};

export const LEVEL_LABELS: Record<number, string> = {
  1: "Newcomer",
  2: "Explorer",
  3: "Builder",
  4: "Expert",
  5: "Master",
};

export const LEVEL_ICONS: Record<number, string> = {
  1: "🌱",
  2: "🔍",
  3: "🔨",
  4: "⚡",
  5: "🏆",
};

export const LEVEL_PERKS: Record<number, string[]> = {
  1: ["Swipe on job matches", "Build your Skills Vault", "Start experience mining"],
  2: ["XP daily streak bonuses", "Earn achievement badges", "Track applications"],
  3: ["Resume generation", "Match explanations", "Advanced mining bonuses"],
  4: ["Priority matching tier", "Story-skill linking", "Full badge showcase"],
  5: ["Master rank — all features unlocked", "Max XP multipliers"],
};

export function getLevelFromXP(xp: number): number {
  let level = 1;
  for (const [lvl, threshold] of Object.entries(LEVEL_THRESHOLDS)) {
    if (xp >= threshold) level = Number(lvl);
  }
  return level;
}

export function getXPForNextLevel(currentLevel: number): number | null {
  const nextLevel = currentLevel + 1;
  return LEVEL_THRESHOLDS[nextLevel] ?? null;
}

export function getLevelProgress(xp: number, level: number): number {
  const currentThreshold = LEVEL_THRESHOLDS[level] ?? 0;
  const nextThreshold = LEVEL_THRESHOLDS[level + 1];
  if (nextThreshold == null) return 1; // Max level
  const range = nextThreshold - currentThreshold;
  if (range <= 0) return 1;
  return Math.min(1, (xp - currentThreshold) / range);
}

// --- Badge Configuration ---

export interface BadgeDefinition {
  id: string;
  name: string;
  description: string;
  icon: string;
}

export const BADGE_DEFINITIONS: BadgeDefinition[] = [
  {
    id: "first_skill",
    name: "First Skill",
    description: "Added your first skill to the vault",
    icon: "gem",
  },
  {
    id: "profile_complete",
    name: "Profile Complete",
    description: "Completed your user profile",
    icon: "check-circle",
  },
  {
    id: "ten_applications",
    name: "10 Applications",
    description: "Applied to 10 jobs",
    icon: "envelope",
  },
  {
    id: "first_tier1_match",
    name: "First Tier 1 Match",
    description: "Got your first Tier 1 match",
    icon: "trophy",
  },
  {
    id: "mining_master",
    name: "Mining Master",
    description: "Completed 5 mining sessions",
    icon: "pickaxe",
  },
  {
    id: "story_teller",
    name: "Story Teller",
    description: "Linked 10 stories to skills",
    icon: "scroll",
  },
];

export function getBadgeDefinition(badgeId: string): BadgeDefinition | undefined {
  return BADGE_DEFINITIONS.find((b) => b.id === badgeId);
}

// --- Data Access ---

export async function getUserGamification(): Promise<UserGamification | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from("user_gamification")
    .select("*")
    .eq("user_id", user.id)
    .maybeSingle();

  if (error) throw new Error(`Failed to load gamification: ${error.message}`);
  return data;
}

export async function ensureGamificationRow(): Promise<UserGamification> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  // Try to get existing row
  const { data: existing } = await supabase
    .from("user_gamification")
    .select("*")
    .eq("user_id", user.id)
    .maybeSingle();

  if (existing) return existing;

  // Create new row
  const { data, error } = await supabase
    .from("user_gamification")
    .insert({ user_id: user.id })
    .select()
    .single();

  if (error) throw new Error(`Failed to create gamification: ${error.message}`);
  return data;
}

// --- Streak Logic ---

/**
 * Update the user's activity streak. Call this when the user performs
 * any engagement action. Handles consecutive-day tracking.
 * Uses atomic server-side function to prevent race conditions.
 */
export async function updateStreak(): Promise<UserGamification> {
  const { data, error } = await supabase.rpc("update_user_streak");

  if (error) throw new Error(`Failed to update streak: ${error.message}`);
  return data;
}

// --- XP Logic ---

/**
 * Award XP for an action. Updates streak, computes level, logs event.
 * Uses atomic server-side function to prevent race conditions.
 * Returns the updated gamification state.
 */
export async function awardXP(action: string): Promise<UserGamification> {
  const xpAmount = XP_REWARDS[action];
  if (!xpAmount) throw new Error(`Unknown XP action: ${action}`);

  const { data, error } = await supabase.rpc("award_user_xp", {
    p_action: action,
    p_xp_amount: xpAmount,
  });

  if (error) throw new Error(`Failed to award XP: ${error.message}`);
  return data;
}

// --- Badge Logic ---

/**
 * Award a badge to the user if they don't already have it.
 * Uses atomic server-side function to prevent race conditions.
 * Returns true if the badge was newly awarded.
 */
export async function awardBadge(badgeId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc("award_user_badge", {
    p_badge_id: badgeId,
  });

  if (error) throw new Error(`Failed to award badge: ${error.message}`);
  return data;
}

/**
 * Check and award any badges the user has earned.
 * Queries relevant tables to determine eligibility.
 */
export async function checkAndAwardBadges(): Promise<string[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return [];

  const gam = await ensureGamificationRow();
  const currentBadges: string[] = gam.badges ?? [];
  const newlyAwarded: string[] = [];

  // Check each badge condition
  const checks = await Promise.allSettled([
    // First Skill: has at least 1 skill
    supabase
      .from("user_skills")
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id),
    // Profile Complete: has display_name set
    supabase.from("user_profiles").select("display_name").eq("id", user.id).maybeSingle(),
    // 10 Applications
    supabase
      .from("applications")
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id),
    // First Tier 1 Match: has a swipe decision with tier 1
    supabase
      .from("swipe_decisions")
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id)
      .eq("tier", 1),
    // Mining Master: 5 completed sessions
    supabase
      .from("mining_sessions")
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id)
      .eq("status", "completed"),
    // Story Teller: 10 experiences linked
    supabase
      .from("user_experiences")
      .select("id", { count: "exact", head: true })
      .eq("user_id", user.id),
  ]);

  // first_skill
  if (!currentBadges.includes("first_skill") && checks[0].status === "fulfilled") {
    const count = checks[0].value.count ?? 0;
    if (count >= 1) newlyAwarded.push("first_skill");
  }

  // profile_complete
  if (!currentBadges.includes("profile_complete") && checks[1].status === "fulfilled") {
    const profile = checks[1].value.data;
    if (profile?.display_name) newlyAwarded.push("profile_complete");
  }

  // ten_applications
  if (!currentBadges.includes("ten_applications") && checks[2].status === "fulfilled") {
    const count = checks[2].value.count ?? 0;
    if (count >= 10) newlyAwarded.push("ten_applications");
  }

  // first_tier1_match
  if (!currentBadges.includes("first_tier1_match") && checks[3].status === "fulfilled") {
    const count = checks[3].value.count ?? 0;
    if (count >= 1) newlyAwarded.push("first_tier1_match");
  }

  // mining_master
  if (!currentBadges.includes("mining_master") && checks[4].status === "fulfilled") {
    const count = checks[4].value.count ?? 0;
    if (count >= 5) newlyAwarded.push("mining_master");
  }

  // story_teller
  if (!currentBadges.includes("story_teller") && checks[5].status === "fulfilled") {
    const count = checks[5].value.count ?? 0;
    if (count >= 10) newlyAwarded.push("story_teller");
  }

  // Award newly earned badges atomically via RPC
  for (const badge of newlyAwarded) {
    await awardBadge(badge);
  }

  return newlyAwarded;
}

// --- Daily First-Action Bonuses ---

/**
 * Daily XP bonus multipliers for first-of-day actions.
 * Creates daily check-in habit: first action of each type gets a multiplier.
 * Total XP = base XP * multiplier (e.g., swipe: 5 * 3 = 15 XP).
 */
export const DAILY_BONUS_MULTIPLIERS: Record<string, number> = {
  swipe: 3, // First swipe: 5 * 3 = 15 XP
  complete_mining_session: 2, // First mining: 25 * 2 = 50 XP
};

export const DAILY_BONUS_LABELS: Record<string, string> = {
  swipe: "Daily First Swipe",
  complete_mining_session: "Daily First Mining",
};

/**
 * Calculate bonus XP for a daily first action.
 * Returns the EXTRA XP beyond the base reward (multiplier - 1) * base.
 * Returns 0 if the action has no daily bonus configured.
 */
export function getDailyBonusXP(action: string): number {
  const multiplier = DAILY_BONUS_MULTIPLIERS[action];
  if (!multiplier) return 0;
  const base = XP_REWARDS[action];
  if (!base) return 0;
  return base * (multiplier - 1);
}

/**
 * Check whether today's date (YYYY-MM-DD) matches the given date string.
 */
export function isToday(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const today = new Date().toISOString().slice(0, 10);
  return dateStr.slice(0, 10) === today;
}

/**
 * Fetch the set of actions already performed today by the current user.
 * Queries xp_events for today's date.
 */
export async function getTodayActions(): Promise<Set<string>> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return new Set();

  const today = new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from("xp_events")
    .select("action")
    .eq("user_id", user.id)
    .gte("created_at", `${today}T00:00:00Z`)
    .lt("created_at", `${today}T23:59:59.999Z`);

  if (error) return new Set();
  return new Set((data ?? []).map((e: { action: string }) => e.action));
}

// --- Mining Bonus Rewards (Variable Reinforcement) ---

export interface MiningSessionMetrics {
  userMessageCount: number;
  storiesExtracted: number;
  hasCompleteStory: boolean;
  isNewTopic: boolean;
}

export interface MiningBonus {
  action: string;
  label: string;
  xp: number;
  eligible: boolean;
  awarded: boolean;
}

/**
 * Evaluate mining session bonuses based on quality signals.
 * Eligible bonuses are always awarded — no random roll.
 *
 * Bonuses:
 * - Complete Story: session produced at least one complete STAR story
 * - New Territory: first time mining this topic
 * - Story Rich: 2+ stories extracted from session
 *
 * @param metrics - session metrics to evaluate
 */
export function evaluateMiningBonuses(metrics: MiningSessionMetrics): MiningBonus[] {
  const bonuses: MiningBonus[] = [
    {
      action: "bonus_deep_mining",
      label: "Complete Story",
      xp: XP_REWARDS.bonus_deep_mining,
      eligible: metrics.hasCompleteStory,
      awarded: false,
    },
    {
      action: "bonus_new_territory",
      label: "New Territory",
      xp: XP_REWARDS.bonus_new_territory,
      eligible: metrics.isNewTopic,
      awarded: false,
    },
    {
      action: "bonus_story_rich",
      label: "Story Rich",
      xp: XP_REWARDS.bonus_story_rich,
      eligible: metrics.storiesExtracted >= 2,
      awarded: false,
    },
  ];

  for (const bonus of bonuses) {
    if (bonus.eligible) {
      bonus.awarded = true;
    }
  }

  return bonuses;
}

// --- Proximity Nudge ---

/** Max XP gap to trigger a proximity nudge (within ~2 typical actions) */
export const PROXIMITY_XP_THRESHOLD = 30;

export interface ProximityNudge {
  xpRemaining: number;
  nextLevel: number;
  nextLevelLabel: string;
  suggestion: string;
}

/**
 * Check if user is close to the next level and return a nudge message.
 * Returns null if at max level or not close enough.
 */
export function getProximityNudge(xp: number, level: number): ProximityNudge | null {
  const nextThreshold = LEVEL_THRESHOLDS[level + 1];
  if (nextThreshold == null) return null; // max level

  const xpRemaining = nextThreshold - xp;
  if (xpRemaining <= 0 || xpRemaining > PROXIMITY_XP_THRESHOLD) return null;

  const nextLevel = level + 1;
  const nextLevelLabel = LEVEL_LABELS[nextLevel] ?? `Level ${nextLevel}`;

  // Suggest an action based on how much XP is needed
  let suggestion: string;
  if (xpRemaining <= XP_REWARDS.swipe) {
    suggestion = "one more swipe";
  } else if (xpRemaining <= XP_REWARDS.add_skill) {
    suggestion = "adding a skill";
  } else if (xpRemaining <= XP_REWARDS.complete_mining_session) {
    suggestion = "one more mining session";
  } else {
    suggestion = "a couple more actions";
  }

  return { xpRemaining, nextLevel, nextLevelLabel, suggestion };
}

// --- Recent XP Events ---

export async function getRecentXPEvents(limit = 10): Promise<XPEvent[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return [];

  const { data, error } = await supabase
    .from("xp_events")
    .select("*")
    .eq("user_id", user.id)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) throw new Error(`Failed to load XP events: ${error.message}`);
  return data ?? [];
}
